import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronRight, Package, Store } from "lucide-react";
import { getRkOrders } from "../../network/externalApi";
import { formatLongDate, formatDayName } from "utils/dateFormat";
import RkAccountMenu from "../../components/RkAccountMenu/RkAccountMenu";
import styles from "./OutletOrders.module.scss";

const ORDERS_COUNT = 10;

const pickReturnStatus = (order) => order?.ReturnStatus ?? "";

const pickDate = (order) => order?.DeliveryDate ?? null;

const pickItemCount = (order) =>
  (order?.Items || []).reduce(
    (sum, item) => sum + (Number(item?.OrderedQuantity) || 0),
    0
  );

const pickTonnage = (order) =>
  (order?.Items || []).reduce(
    (sum, item) =>
      sum +
      (Number(item?.OrderedQuantity) || 0) *
        (Number(item?.WeightPerPacket) || 0),
    0
  );

const pickOrderNumber = (order) => order?.OrderNumber ?? null;

const pickMxPoNumber = (order) => order?.MxPoNumber ?? null;

const pickOrderId = (order) => order?.OrderId ?? pickOrderNumber(order);

const isReturnPending = (order) =>
  String(pickReturnStatus(order)).toUpperCase() === "NOT_CREATED";

const formatTonnage = (kg) => {
  if (!kg) return "0kg";
  const rounded = Math.round(kg * 100) / 100;
  return `${rounded}kg`;
};

const OutletOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { outletId: paramOutletId } = useParams();
  const outletId = paramOutletId || location.state?.outletId;
  const outletName = location.state?.outletName || "Outlet";

  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!outletId) {
      setError("Missing outlet");
      setIsLoading(false);
      return;
    }
    (async () => {
      setIsLoading(true);
      setError("");
      try {
        const resp = await getRkOrders({
          outletId,
          ordersCount: ORDERS_COUNT,
        });
        if (cancelled) return;
        const data = resp?.data?.response;
        const list =
          data?.Orders ||
          data?.orders ||
          resp?.data?.Orders ||
          resp?.data?.orders ||
          [];
        setOrders(Array.isArray(list) ? list : []);
      } catch (err) {
        if (cancelled) return;
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load orders"
        );
        setOrders([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [outletId]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className={styles.title}>{outletName}</h1>
        <RkAccountMenu buttonClassName={styles.iconButton} />
      </div>

      <div className={styles.content}>
        <div className={styles.sectionLabel}>
          <span className={styles.sectionLine} />
          <span className={styles.sectionText}>LAST {ORDERS_COUNT} ORDERS</span>
          <span className={styles.sectionLine} />
        </div>

        {isLoading ? (
          <div className={styles.stateText}>Loading orders…</div>
        ) : error ? (
          <div className={`${styles.stateText} ${styles.errorText}`}>
            {error}
          </div>
        ) : orders.length === 0 ? (
          <div className={styles.stateText}>No orders found</div>
        ) : (
          orders.map((order, idx) => {
            const returnPending = isReturnPending(order);
            const meta = returnPending
              ? { label: "Pending", variant: "pending" }
              : { label: "Return marked", variant: "returnMarked" };
            const rawDate = pickDate(order);
            const itemCount = pickItemCount(order);
            const tonnage = pickTonnage(order);
            const orderNumber = pickOrderNumber(order);
            const mxPoNumber = pickMxPoNumber(order);
            const orderId = pickOrderId(order) ?? idx;

            return (
              <div
                key={orderId}
                className={`${styles.orderCard} ${
                  returnPending ? "" : styles.orderCardDisabled
                }`}
              >
                <div className={styles.orderTop}>
                  <div className={styles.orderHeaderContent}>
                    <span
                      className={`${styles.statusPill} ${styles[meta.variant]}`}
                    >
                      {meta.label}
                    </span>

                    <div className={styles.orderDate}>
                      {formatLongDate(rawDate) || rawDate || "—"}
                    </div>
                    <div className={styles.orderDay}>
                      {formatDayName(rawDate)}
                    </div>

                    {orderNumber != null || mxPoNumber != null ? (
                      <div className={styles.orderIdentifiers}>
                        {orderNumber != null ? (
                          <span>
                            Order number: <strong>{orderNumber}</strong>
                          </span>
                        ) : null}
                        {mxPoNumber != null ? (
                          <span>
                            MX PO number: <strong>{mxPoNumber}</strong>
                          </span>
                        ) : null}
                      </div>
                    ) : null}
                  </div>

                  {returnPending ? (
                    <button
                      type="button"
                      className={styles.orderChevron}
                      aria-label="Open order"
                      onClick={() => {
                        const realOrderId = pickOrderId(order);
                        if (realOrderId == null) return;
                        navigate(
                          `/rk/outlets/${outletId}/orders/${realOrderId}/return`,
                          {
                            state: {
                              outletId,
                              outletName,
                              orderId: realOrderId,
                              orderDate: rawDate,
                            },
                          }
                        );
                      }}
                    >
                      <ChevronRight size={22} strokeWidth={2.5} />
                    </button>
                  ) : null}
                </div>

                <div className={styles.orderDivider} />

                <div className={styles.orderMetaRow}>
                  <span className={styles.metaIcon}>
                    <Package size={18} />
                  </span>
                  <span className={styles.metaText}>
                    Item count:{" "}
                    <strong>
                      {itemCount} {itemCount === 1 ? "item" : "items"}
                    </strong>
                  </span>
                </div>

                <div className={styles.orderMetaRow}>
                  <span className={styles.metaIcon}>
                    <Store size={18} />
                  </span>
                  <span className={styles.metaText}>
                    Tonnage: <strong>{formatTonnage(tonnage)}</strong>
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default OutletOrders;
